import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Alert,
  MenuItem,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Collapse,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { Account, AccountService } from '../types/account';
import AccountFormDialog from '../components/AccountFormDialog';
import ServiceFormDialog from '../components/ServiceFormDialog';
import MoveToAccountDialog from '../components/MoveToAccountDialog';
import apiService from '../services/api';

const AccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [accountServices, setAccountServices] = useState<Map<string, AccountService[]>>(new Map());
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState<Set<string>>(new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [serviceFormOpen, setServiceFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<AccountService | null>(null);
  const [selectedAccountForService, setSelectedAccountForService] = useState<Account | null>(null);
  const [moveServiceOpen, setMoveServiceOpen] = useState(false);
  const [serviceToMove, setServiceToMove] = useState<AccountService | null>(null);
  const [moveSourceAccountId, setMoveSourceAccountId] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  
  // Search and filters
  const [hasSearched, setHasSearched] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('organisation_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Snackbar helper
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Status color helper
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  // Service type color helper
  const getServiceTypeColor = (serviceType: string) => {
    switch (serviceType) {
      case 'nbn': return 'primary';
      case 'mobile': return 'secondary';
      case 'voice': return 'success';
      case 'sonweb': return 'info';
      case 'data': return 'warning';
      default: return 'default';
    }
  };

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = {
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (searchQuery) {
        params.q = searchQuery;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await apiService.getAccounts(params);
      setAccounts(response.accounts || []);
      setTotalAccounts(response.total || 0);
    } catch (error: any) {
      console.error('Failed to fetch accounts:', error);
      showSnackbar(
        error.response?.data?.detail || 'Failed to load accounts',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortOrder, searchQuery, statusFilter]);

  // Fetch accounts only after user has clicked Search
  useEffect(() => {
    if (hasSearched) fetchAccounts();
  }, [hasSearched, fetchAccounts]);

  // Handle search
  const handleSearch = () => {
    setHasSearched(true);
    setPage(0);
  };

  // Handle clear filters (reset to default: no records shown)
  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setHasSearched(false);
    setPage(0);
    setAccounts([]);
    setTotalAccounts(0);
    setExpandedAccounts(new Set());
    setAccountServices(new Map());
  };

  // Toggle account expansion
  const toggleAccountExpanded = (accountId: string) => {
    setExpandedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
        // Fetch services when expanding
        fetchAccountServices(accountId);
      }
      return next;
    });
  };

  // Fetch account services
  const fetchAccountServices = async (accountId: string) => {
    if (accountServices.has(accountId)) return; // Already fetched
    
    setServicesLoading(prev => new Set(prev).add(accountId));
    try {
      const response = await apiService.getAccountServices(accountId, {});
      setAccountServices(prev => new Map(prev).set(accountId, response.services || []));
    } catch (error: any) {
      showSnackbar('Failed to load services: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setServicesLoading(prev => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  };

  // Handle create account
  const handleCreateAccount = async (accountData: any) => {
    try {
      await apiService.createAccount(accountData);
      showSnackbar('Account created successfully', 'success');
      setFormOpen(false);
      setSelectedAccount(null);
      fetchAccounts();
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.message || 'Failed to create account');
    }
  };

  // Handle update account
  const handleUpdateAccount = async (accountData: any) => {
    if (!selectedAccount) return;
    
    try {
      await apiService.updateAccount(selectedAccount.id, accountData);
      showSnackbar('Account updated successfully', 'success');
      setFormOpen(false);
      setSelectedAccount(null);
      fetchAccounts();
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.message || 'Failed to update account');
    }
  };

  // Handle delete account
  const handleDeleteAccount = async (accountId: string) => {
    if (!window.confirm('Are you sure you want to delete this account?')) {
      return;
    }

    try {
      await apiService.deleteAccount(accountId);
      showSnackbar('Account deleted successfully', 'success');
      fetchAccounts();
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.detail || 'Failed to delete account',
        'error'
      );
    }
  };

  // Handle edit account
  const handleEditAccount = (account: Account) => {
    setSelectedAccount(account);
    setFormOpen(true);
  };

  // Handle service form save
  const handleServiceFormSave = async () => {
    if (!selectedAccountForService) return;
    
    // Refresh services for the account
    setAccountServices(prev => {
      const next = new Map(prev);
      next.delete(selectedAccountForService.id);
      return next;
    });
    await fetchAccountServices(selectedAccountForService.id);
    showSnackbar('Service saved successfully', 'success');
  };

  const handleServiceTransferred = async (targetAccountId: string) => {
    if (!serviceToMove || !moveSourceAccountId) return;
    await apiService.transferAccountService(moveSourceAccountId, serviceToMove.id, targetAccountId);
    setAccountServices((prev) => {
      const next = new Map(prev);
      next.delete(moveSourceAccountId);
      return next;
    });
    await fetchAccountServices(moveSourceAccountId);
    if (targetAccountId !== moveSourceAccountId) {
      setAccountServices((prev) => {
        const next = new Map(prev);
        next.delete(targetAccountId);
        return next;
      });
      await fetchAccountServices(targetAccountId);
    }
    setMoveServiceOpen(false);
    setServiceToMove(null);
    setMoveSourceAccountId(null);
    showSnackbar('Service moved to the selected account.', 'success');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Accounts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
                    onClick={() => setFormOpen(true)}
        >
          Add Account
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
              >
                Search
              </Button>
              {hasSearched && (
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchAccounts()}>
                  Refresh
                </Button>
              )}
              {(searchQuery || statusFilter || hasSearched) && (
                <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearFilters}>
                  Clear filters
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Accounts Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !hasSearched ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">
              Enter search or select a filter and click Search to see results.
            </Typography>
          </Box>
        ) : accounts.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">No accounts found</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50px"></TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Services</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((account) => (
                    <React.Fragment key={account.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleAccountExpanded(account.id)}
                          >
                            {expandedAccounts.has(account.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {account.display_name}
                            </Typography>
                            {account.sonic_account_number && (
                              <Typography variant="caption" color="textSecondary">
                                #{account.sonic_account_number}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {account.full_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {account.email ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography variant="body2">{account.email}</Typography>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {account.mobile_number ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography variant="body2">{account.mobile_number}</Typography>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={account.status}
                            size="small"
                            color={getStatusColor(account.status) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${account.service_count} service${account.service_count !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/accounts/${account.id}`)}
                            title="View account"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleEditAccount(account)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteAccount(account.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Services Row */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedAccounts.has(account.id) ? 1 : 0 }}>
                          <Collapse in={expandedAccounts.has(account.id)} timeout="auto">
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Services for {account.display_name}
                              </Typography>
                              
                              {servicesLoading.has(account.id) ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : (
                                <Grid container spacing={2}>
                                  {(accountServices.get(account.id) || []).map((service) => (
                                    <Grid item xs={12} md={6} key={service.id}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Stack spacing={1}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                              <Chip
                                                label={
                                                  (service.service_type_name && String(service.service_type_name).trim()) ||
                                                  service.service_type.toUpperCase()
                                                }
                                                size="small"
                                                color={getServiceTypeColor(service.service_type) as any}
                                              />
                                              <Chip
                                                label={service.status}
                                                size="small"
                                                color={getStatusColor(service.status) as any}
                                              />
                                            </Box>
                                            
                                            <Typography variant="body2">
                                              <strong>Service ID:</strong> {service.service_id}
                                            </Typography>
                                            
                                            {service.wsm_service_id && (
                                              <Typography variant="body2">
                                                <strong>WSM ID:</strong> {service.wsm_service_id}
                                              </Typography>
                                            )}
                                            
                                            {service.nbn_carrier_id && (
                                              <Typography variant="body2">
                                                <strong>NBN Carrier:</strong> {service.nbn_carrier_id}
                                              </Typography>
                                            )}
                                            
                                            {service.nbn_avc && (
                                              <Typography variant="body2">
                                                <strong>AVC:</strong> {service.nbn_avc}
                                              </Typography>
                                            )}
                                            
                                            {service.notes && (
                                              <Typography variant="body2" color="textSecondary">
                                                {service.notes}
                                              </Typography>
                                            )}
                                            
                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                              <Button
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={() => {
                                    setSelectedAccountForService(account);
                                    setSelectedService(service);
                                    setServiceFormOpen(true);
                                  }}
                                              >
                                                Edit
                                              </Button>
                                              <Button
                                                size="small"
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={async () => {
                                                if (window.confirm(`Are you sure you want to delete service ${service.service_id}?`)) {
                                                  try {
                                                    await apiService.deleteService(service.id);
                                                    showSnackbar('Service deleted successfully', 'success');
                                                    // Refresh services for this account
                                                    setAccountServices(prev => {
                                                      const next = new Map(prev);
                                                      next.delete(account.id);
                                                      return next;
                                                    });
                                                    fetchAccountServices(account.id);
                                                  } catch (error: any) {
                                                    showSnackbar('Failed to delete service: ' + (error.message || 'Unknown error'), 'error');
                                                  }
                                                }
                                              }}
                                              >
                                                Delete
                                              </Button>
                                            </Box>
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                                  
                                  {/* Add Service Button */}
                                  <Grid item xs={12} md={6}>
                                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 150 }}>
                                      <Button
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                    setSelectedAccountForService(account);
                                    setSelectedService(null);
                                    setServiceFormOpen(true);
                                  }}
                                      >
                                        Add Service
                                      </Button>
                                    </Card>
                                  </Grid>
                                </Grid>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {hasSearched && (
              <TablePagination
                component="div"
                count={totalAccounts}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Account Form Dialog */}
      <AccountFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedAccount(null);
        }}
        onSave={selectedAccount ? handleUpdateAccount : handleCreateAccount}
        account={selectedAccount}
      />

      {/* Service Form Dialog */}
      {selectedAccountForService && (
        <ServiceFormDialog
          open={serviceFormOpen}
          onClose={() => {
            setServiceFormOpen(false);
            setSelectedService(null);
            setSelectedAccountForService(null);
          }}
          onSave={handleServiceFormSave}
          accountId={selectedAccountForService.id}
          service={selectedService}
          onRequestMove={
            selectedService
              ? () => {
                  const svc = selectedService;
                  const srcId = selectedAccountForService.id;
                  setServiceFormOpen(false);
                  setSelectedService(null);
                  setSelectedAccountForService(null);
                  setMoveSourceAccountId(srcId);
                  setServiceToMove(svc);
                  setMoveServiceOpen(true);
                }
              : undefined
          }
        />
      )}
      {moveSourceAccountId && (
        <MoveToAccountDialog
          open={moveServiceOpen}
          onClose={() => {
            setMoveServiceOpen(false);
            setServiceToMove(null);
            setMoveSourceAccountId(null);
          }}
          excludeAccountId={moveSourceAccountId}
          title="Move service to another account"
          subtitle={serviceToMove ? `Service ID: ${serviceToMove.service_id}` : undefined}
          onTransferred={handleServiceTransferred}
        />
      )}
    </Box>
  );
};

export default AccountsPage;
